import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useHealth, useMe } from "../lib/api";
import { signInHref } from "../lib/auth";

/**
 * Plenum's own sign-in page (docs/proposals/auth-native-login.md, option B). It sets the scene and
 * hands off to Cloudflare Access for the one-time code; Access returns the visitor to the page they
 * asked for through /api/session/return. Rendered without the app shell.
 */
export function LoginPage() {
  const { next } = useSearch({ from: "/login" });
  const me = useMe();
  const health = useHealth();
  const [email, setEmail] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    window.location.assign(signInHref(next));
  }

  return (
    <main
      className="flex min-h-full items-center justify-center px-4 py-10"
      style={{ background: "var(--ink)", color: "var(--paper)" }}
      data-testid="login-page"
    >
      <div className="panel w-full max-w-[400px] rounded-[10px] border p-7">
        <img
          src="/brand/plenum-logo.svg"
          alt="Plenum"
          width={280}
          height={69}
          className="mb-5 block"
        />
        <p className="mb-6 text-[13.5px] leading-relaxed" style={{ color: "var(--muted)" }}>
          Plenum maps North American gas turbine sites and ranks each one by the output that
          evaporative inlet cooling would recover in its own climate, with turbine models, existing
          cooling and plant contacts drawn from public records. Built by{" "}
          <a
            href="https://c12.space"
            target="_blank"
            rel="noreferrer"
            className="font-semibold"
            style={{ color: "var(--gold)" }}
          >
            C12
          </a>{" "}
          for BBM-CPG.
        </p>
        {me.data ? (
          <p className="text-[13.5px]" data-testid="login-signed-in">
            You are signed in as <span className="font-semibold">{me.data.email}</span>.{" "}
            <a href={next} className="font-semibold" style={{ color: "var(--gold)" }}>
              Continue
            </a>
          </p>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-[12.5px]" style={{ color: "var(--muted)" }}>
              Work email
              <input
                className="input w-full py-1.5"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@bbm-cpg.com"
                data-testid="login-email"
              />
            </label>
            <button type="submit" className="btn btn-primary w-full" data-testid="login-submit">
              Send me a code
            </button>
            <p className="text-[12px] leading-relaxed" style={{ color: "var(--muted-2)" }}>
              Cloudflare Access will ask for this address again and email you a six-digit code.
              Enter it there and you land back in Plenum; the session lasts 30 days on this browser.
              Only C12 and BBM-CPG addresses are admitted.
            </p>
          </form>
        )}
        <p className="num mt-6 text-[11px]" style={{ color: "var(--muted-2)" }}>
          {health.data ? `Build ${health.data.version}` : ""}
        </p>
      </div>
    </main>
  );
}
